import '../MortgageBuyOrDie.css';

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Select, useSelect, NumberInput, Modal } from '@vtblife/uikit';
import { Rooms } from '@search/filter-enums/enums/Rooms';

import { validateFormFields } from '../helpers/validate-helper';
import { cnMrtgBuyOrDie, FormType } from '../MortgageBuyOrDieModal';
import { regionsEnumerationDie } from '../helpers/mapRegionToSelect';
import { MortgageBuyOrDieFormField } from './MortgageBuyOrDieFormField';

const ROOM_OPTIONS: { label: string; value: Rooms }[] = [
    { label: '1', value: Rooms.ROOM_1 },
    { label: '2', value: Rooms.ROOM_2 },
    { label: '3', value: Rooms.ROOM_3 },
    { label: '4+', value: Rooms.ROOM_4 },
    { label: 'Студия', value: Rooms.STUDIO },
];

export function MortgageBuyOrDieModalForm({
    onCalculate,
    isMobile,
    form,
    setForm,
    mortgagePercent
}: {
    isMobile?: boolean;
    form: FormType;
    setForm: React.Dispatch<React.SetStateAction<FormType>>;
    onCalculate(): void;
    mortgagePercent: number;
}) {
    const setOwnAmount = useCallback((newValue: number) => setForm((state) => ({ ...state, ownAmount: ! isNaN(newValue) ? newValue : 0 })), []);
    const setPropertyCost = useCallback((newValue: number) => setForm((state) => ({ ...state, propertyCost: ! isNaN(newValue) ? newValue : 0 })), []);
    const setRent = useCallback((newValue: number) => setForm((state) => ({ ...state, rent: ! isNaN(newValue) ? newValue : 0 })), []);
    const setComfortInvoice = useCallback((newValue: number) => setForm((state) => ({ ...state, comfortInvoice: ! isNaN(newValue) ? newValue : 0 })), []);
    const setRoomType = useCallback((roomType: FormType['roomType']) => () => {
        setForm((state) => {
            if (state.roomType === roomType) return { ...state, roomType: undefined };
            return ({ ...state, roomType });
        });
    }, []);
    const regionsSelect = useSelect(
        newValue => setForm((state) => ({ ...state, region: newValue })),
        {
            selected: form.region, // initial value
            texts: regionsEnumerationDie.map(option => ({ value: option.value, text: option.label }))
        }
    );

    const [ formTouched, setFormTouched ] = useState({
        rent: false,
        comfortInvoice: false,
        propertyCost: false,
        ownAmount: false,
    });

    const validateForm = useCallback((isFinalValidate?: boolean) => {
        return validateFormFields({
            touched: isFinalValidate ?
                { rent: true, comfortInvoice: true, propertyCost: true, ownAmount: true } : formTouched,
            formValues: form,
            mortgagePercent: isFinalValidate ? mortgagePercent : undefined
        });
    }, [ formTouched, form, mortgagePercent ]);
    const [ formValidMessages, setFormValidMessages ] = useState(validateForm);

    useEffect(() => setFormValidMessages(validateForm()), [ validateForm ]);

    const setRentTouched = useCallback(() => setFormTouched((state) => ({ ...state, rent: true })), []);
    const setPropertyCostTouched = useCallback(() => setFormTouched((state) => ({ ...state, propertyCost: true })), []);
    const setOwnAmountTouched = useCallback(() => setFormTouched((state) => ({ ...state, ownAmount: true })), []);
    const setComfortInvoiceTouched = useCallback(() => setFormTouched((state) => ({ ...state, comfortInvoice: true })), []);

    const handleValidToCalculate = useCallback(() => {
        const isFinalValidate = true;
        const isValidFormFields = validateForm(isFinalValidate);

        if (! isValidFormFields.rent.isError && ! isValidFormFields.comfortInvoice.isError &&
            ! isValidFormFields.propertyCost && ! isValidFormFields.ownAmount.isError
        ) {
            onCalculate();
        } else {
            setFormValidMessages(isValidFormFields);
        }
    }, [ onCalculate, validateForm ]);

    return (<>
        <form className={cnMrtgBuyOrDie('form')}>
            <MortgageBuyOrDieFormField text='Где вы&nbsp;собираетесь купить квартиру?'>
                <Select native={isMobile} mode='single' onChange={regionsSelect.handleChange}>
                    <Select.Button size='l' dataTest='regions-trigger'>
                        {regionsSelect.selectedText}
                    </Select.Button>
                    <Select.Content>
                        {regionsEnumerationDie.map(option => (
                            <Select.Option
                                dataTest={`ceilHeight-${option.value}`}
                                key={option.value}
                                value={option.value}
                                selected={regionsSelect.is(option.value)}
                            >
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select.Content>
                </Select>
            </MortgageBuyOrDieFormField>
            <MortgageBuyOrDieFormField text='Сколько комнат:'>
                <div className={cnMrtgBuyOrDie('form-rooms')}>
                    {ROOM_OPTIONS.map(({ value, label }) => (
                        <Button
                            variant='secondary'
                            size='m'
                            key={value}
                            selected={form.roomType === value}
                            onClick={setRoomType(value)}
                        >
                            {label}
                        </Button>
                    ))}
                </div>
            </MortgageBuyOrDieFormField>
            <div className={cnMrtgBuyOrDie('form-inputs')}>
                <MortgageBuyOrDieFormField text='Стоимость квартиры:'>
                    <NumberInput
                        dataTest='mortgage-die-set-propertyCost'
                        size='l'
                        inputMode='numeric'
                        onChange={setPropertyCost}
                        onBlur={setPropertyCostTouched}
                        value={form.propertyCost}
                        postfix='₽'
                        hint={formValidMessages.propertyCost}
                        error={Boolean(formValidMessages.propertyCost)}
                    />
                </MortgageBuyOrDieFormField>
                <MortgageBuyOrDieFormField text='Уже накопили:'>
                    <NumberInput
                        dataTest='mortgage-die-set-amount'
                        size='l'
                        inputMode='numeric'
                        postfix='₽'
                        onChange={setOwnAmount}
                        onBlur={setOwnAmountTouched}
                        value={form.ownAmount}
                        hint={formValidMessages.ownAmount.text}
                        error={formValidMessages.ownAmount.isError}
                    />
                </MortgageBuyOrDieFormField>
                <MortgageBuyOrDieFormField text='Арендная плата за&nbsp;съём жилья:'>
                    <NumberInput
                        dataTest='mortgage-die-set-rent'
                        size='l'
                        onChange={setRent}
                        postfix='₽'
                        value={form.rent}
                        onBlur={setRentTouched}
                        hint={formValidMessages.rent.text}
                        error={formValidMessages.rent.isError}
                    />
                </MortgageBuyOrDieFormField>
                <MortgageBuyOrDieFormField text='Комфортный платёж по&nbsp;ипотеке:'>
                    <NumberInput
                        dataTest='mortgage-die-set-invoice'
                        size='l'
                        onChange={setComfortInvoice}
                        postfix='₽'
                        value={form.comfortInvoice}
                        onBlur={setComfortInvoiceTouched}
                        hint={formValidMessages.comfortInvoice.text}
                        error={formValidMessages.comfortInvoice.isError}
                    />
                </MortgageBuyOrDieFormField>
            </div>
        </form>
        {! isMobile ? (
            <Modal.Actions>
                <Button
                    fullWidth
                    variant='accent'
                    onClick={handleValidToCalculate}
                >Рассчитать</Button>
            </Modal.Actions>
        ) : null}
        {isMobile ? (
            <Modal.StickyActions>
                <Button
                    fullWidth
                    variant='accent'
                    onClick={handleValidToCalculate}
                >Рассчитать</Button>
            </Modal.StickyActions>
        ) : null}
    </>);
}
